import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DateTimePicker } from '@/components/ui/datetime-picker';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Plus,
  Trash2,
  Package,
  FileText,
} from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface CreateLayawayTabProps {
  // Items and inventory
  filteredAndSortedItems: CreditSaleItem[];
  itemSearchTerm: string;
  setItemSearchTerm: (value: string) => void;
  sortBy: string;
  setSortBy: (value: string) => void;

  // Cart
  cart: CreditSaleCartItem[];
  addToCart: (item: CreditSaleItem, quantity?: number, unitPrice?: number, vatEnabled?: boolean) => void;
  removeFromCart: (id: string) => void;
  updateCartQuantity: (id: string, quantity: number) => void;
  updateCartItemPrice: (id: string, price: number) => void;
  updateCartItemVat: (id: string, vatEnabled: boolean) => void;

  // Customer
  customer: { name: string; phone: string; address: string };
  setCustomer: (customer: { name: string; phone: string; address: string }) => void;
  customerOptions: Array<{ id: string; name: string; phone?: string; address?: string }>;
  onCreateCustomer: (customer: { name: string; phone: string; address: string }) => void;
  branches: Array<{ id: string; name: string; is_main?: boolean; isMain?: boolean }>;
  selectedBranch: string;
  setSelectedBranch: (value: string) => void;
  isAdmin: boolean;

  // Payment
  productHandling: 'hold' | 'handover';
  setProductHandling: (value: 'hold' | 'handover') => void;
  paymentScheduleMonths: number;
  setPaymentScheduleMonths: (value: number) => void;
  initialPayment: number;
  setInitialPayment: (value: number) => void;
  paymentMethod: string;
  setPaymentMethod: (value: string) => void;
  notes: string;
  setNotes: (value: string) => void;
  layawayDate: Date;
  setLayawayDate: (value: Date) => void;
  systemTimezone: string;

  // Totals
  cartSubtotal: number;
  cartVAT: number;
  cartTotal: number;
  vatPercentage: number;
  currencySymbol: string;

  // Actions
  createLayaway: () => void;
  isCreating: boolean;
  onDirtyStateChange: (dirty: boolean) => void;
}

interface CreditSaleItem {
  id: string;
  name?: string;
  sku?: string;
  barcode?: string;
  buying_price?: string | number;
  selling_price?: string | number;
  vat_enabled?: number | boolean;
  availableQuantity?: number;
}

interface CreditSaleCartItem {
  id: string;
  name: string;
  sku: string;
  barcode?: string;
  buying_price?: number | string;
  price: number | string;
  quantity: number;
  vat_enabled?: number | boolean;
}

export const CreateLayawayTab: React.FC<CreateLayawayTabProps> = ({
  filteredAndSortedItems,
  itemSearchTerm,
  setItemSearchTerm,
  sortBy,
  setSortBy,
  cart,
  addToCart,
  removeFromCart,
  updateCartQuantity,
  updateCartItemPrice,
  updateCartItemVat,
  customer,
  setCustomer,
  customerOptions,
  onCreateCustomer,
  branches,
  selectedBranch,
  setSelectedBranch,
  isAdmin,
  productHandling,
  setProductHandling,
  paymentScheduleMonths,
  setPaymentScheduleMonths,
  initialPayment,
  setInitialPayment,
  paymentMethod,
  setPaymentMethod,
  notes,
  setNotes,
  systemTimezone,
  layawayDate,
  setLayawayDate,
  cartSubtotal,
  cartVAT,
  cartTotal,
  vatPercentage,
  currencySymbol,
  createLayaway,
  isCreating,
  onDirtyStateChange,
}) => {
  const itemNameInputRef = useRef<HTMLInputElement>(null);
  const remainingBalance = Math.max(0, cartTotal - initialPayment);
  const monthlyPayment = paymentScheduleMonths > 0 ? remainingBalance / paymentScheduleMonths : 0;
  const [showAddCustomerDialog, setShowAddCustomerDialog] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    phone: '',
    address: '',
  });
  const [entryItemId, setEntryItemId] = useState<string>('');
  const [entryQuantityText, setEntryQuantityText] = useState('1');
  const [entryPriceText, setEntryPriceText] = useState('');
  const [entryVatEnabled, setEntryVatEnabled] = useState(false);
  const [entryActiveField, setEntryActiveField] = useState<'name' | null>(null);
  const [entrySuggestionIndex, setEntrySuggestionIndex] = useState(0);
  const entrySuggestionsRef = useRef<HTMLDivElement>(null);
  const hasUnsavedChanges = Boolean(
    cart.length > 0 ||
    customer.name.trim() ||
    customer.phone.trim() ||
    customer.address.trim() ||
    productHandling !== 'hold' ||
    paymentScheduleMonths !== 1 ||
    initialPayment > 0 ||
    paymentMethod !== 'cash' ||
    notes.trim() ||
    itemSearchTerm.trim() ||
    entryItemId ||
    entryQuantityText !== '1' ||
    entryPriceText.trim() ||
    entryVatEnabled
  );

  useEffect(() => {
    onDirtyStateChange(hasUnsavedChanges);
  }, [hasUnsavedChanges, onDirtyStateChange]);

  const entrySuggestions = useMemo(() => {
    const query = itemSearchTerm.trim().toLowerCase();
    if (!query) return [];
    return filteredAndSortedItems
      .filter((item) => {
        const availableQty = Number(item.availableQuantity ?? 0);
        if (availableQty <= 0) return false;
        const name = item?.name?.toLowerCase?.() || '';
        const sku = item?.sku?.toLowerCase?.() || '';
        const barcode = (item?.barcode || '').toLowerCase();
        return name.includes(query) || sku.includes(query) || barcode.includes(query);
      })
      .slice(0, 12);
  }, [filteredAndSortedItems, itemSearchTerm]);

  const selectedEntryItem = useMemo(() => {
    if (entryItemId) {
      return filteredAndSortedItems.find((item) => item.id === entryItemId) || null;
    }

    const query = itemSearchTerm.trim().toLowerCase();
    if (!query) return null;
    return (
      filteredAndSortedItems.find((item) => (item.name || '').toLowerCase() === query && Number(item.availableQuantity ?? 0) > 0) ||
      filteredAndSortedItems.find((item) => (item.sku || '').toLowerCase() === query && Number(item.availableQuantity ?? 0) > 0) ||
      filteredAndSortedItems.find((item) => (item.barcode || '').toLowerCase() === query && Number(item.availableQuantity ?? 0) > 0) ||
      null
    );
  }, [entryItemId, filteredAndSortedItems, itemSearchTerm]);

  const toPrice = (value: string) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const toQty = (value: string) => {
    const parsed = parseInt(value, 10);
    if (!Number.isFinite(parsed)) return 0;
    return Math.max(1, parsed);
  };

  const selectedEntrySku = selectedEntryItem?.sku || '';
  const selectedEntryBarcode = selectedEntryItem?.barcode || '';
  const selectedEntryAverageCost = Number(selectedEntryItem?.buying_price || 0);

  const entryLineTotal = useMemo(() => {
    const unitPrice = toPrice(entryPriceText);
    const quantity = toQty(entryQuantityText);
    const subtotal = unitPrice * quantity;
    if (!entryVatEnabled || vatPercentage <= 0) return subtotal;
    return subtotal + (subtotal * vatPercentage) / 100;
  }, [entryPriceText, entryQuantityText, entryVatEnabled, vatPercentage]);

  useEffect(() => {
    if (entryActiveField === 'name' && entrySuggestions.length > 0 && entrySuggestionsRef.current) {
      const target = entrySuggestionsRef.current.querySelector<HTMLElement>(
        `[data-suggestion-index="${entrySuggestionIndex}"]`
      );
      target?.scrollIntoView({ block: 'nearest' });
    }
  }, [entryActiveField, entrySuggestionIndex, entrySuggestions.length]);

  useEffect(() => {
    setEntrySuggestionIndex(0);
  }, [itemSearchTerm]);

  const handleSelectEntryItem = (item: CreditSaleItem) => {
    if (!item) return;
    if (Number(item.availableQuantity ?? 0) <= 0) {
      return;
    }
    setEntryItemId(item.id);
    setItemSearchTerm(item.name || '');
    const defaultPrice = Number(item.selling_price);
    setEntryPriceText(Number.isFinite(defaultPrice) ? defaultPrice.toString() : '');
    setEntryQuantityText('1');
    setEntryVatEnabled(Boolean(item.vat_enabled));
    setEntryActiveField(null);
  };

  const handleCommitEntryItem = () => {
    if (!selectedEntryItem) {
      return;
    }

    const quantityToAdd = toQty(entryQuantityText);
    const unitPrice = toPrice(entryPriceText);
    const availableQty = selectedEntryItem.availableQuantity ?? 0;
    const inCartQty = cart.find((cartItem) => cartItem.id === selectedEntryItem.id)?.quantity ?? 0;

    if (quantityToAdd <= 0) {
      return;
    }

    if (availableQty <= 0) {
      return;
    }

    if (inCartQty + quantityToAdd > availableQty) {
      return;
    }

    addToCart(selectedEntryItem, quantityToAdd, unitPrice, entryVatEnabled);
    setEntryItemId('');
    setEntryQuantityText('1');
    setEntryPriceText('');
    setEntryVatEnabled(false);
    setEntryActiveField(null);
    setItemSearchTerm('');
    setEntrySuggestionIndex(0);
    requestAnimationFrame(() => {
      itemNameInputRef.current?.focus();
    });
  };

  const handleEntryKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const hasSuggestions = entryActiveField === 'name' && itemSearchTerm.trim() && entrySuggestions.length > 0;

    if (hasSuggestions) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setEntrySuggestionIndex((prev) => (prev + 1) % entrySuggestions.length);
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setEntrySuggestionIndex((prev) => (prev - 1 + entrySuggestions.length) % entrySuggestions.length);
        return;
      }

      if (event.key === 'Enter') {
        const selected = entrySuggestions[entrySuggestionIndex];
        if (selected) {
          event.preventDefault();
          handleSelectEntryItem(selected);
          return;
        }
      }
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      handleCommitEntryItem();
    }
  };

  const lineTotal = (price: number, quantity: number, vatEnabled?: boolean) => {
    const subtotal = (Number(price) || 0) * (Number(quantity) || 0);
    if (!vatEnabled || vatPercentage <= 0) return subtotal;
    return subtotal + (subtotal * vatPercentage) / 100;
  };

  const handleSelectCustomer = (customerId: string) => {
    setSelectedCustomerId(customerId);
    const selected = customerOptions.find((option) => option.id === customerId);
    if (!selected) return;
    setCustomer({
      name: selected.name || '',
      phone: selected.phone || '',
      address: selected.address || '',
    });
  };

  const handleSaveCustomer = () => {
    if (!newCustomer.name.trim()) return;
    const payload = {
      name: newCustomer.name.trim(),
      phone: newCustomer.phone.trim(),
      address: newCustomer.address.trim(),
    };
    onCreateCustomer(payload);
    setCustomer(payload);
    setSelectedCustomerId('');
    setNewCustomer({ name: '', phone: '', address: '' });
    setShowAddCustomerDialog(false);
  };

  return (
    <div className="w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-col gap-3 border-b border-slate-200 bg-white px-5 py-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-slate-950">Create Credit Sale</h1>
            <p className="text-sm text-slate-500">Add customer, payment terms, and invoice items.</p>
          </div>
        </div>
        <Badge variant="outline" className="w-fit rounded-lg border-blue-100 bg-blue-50 px-3 py-1.5 text-base font-semibold text-blue-700">
          Total: {currencySymbol}{cartTotal.toFixed(2)}
        </Badge>
      </div>

      <form className="space-y-4 p-4" autoComplete="off" onSubmit={(event) => event.preventDefault()}>
        <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-3">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800">
            <span className="h-2 w-2 rounded-full bg-blue-500" />
            Sale Details
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-5">
            {isAdmin && (
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Branch *</Label>
                <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                  <SelectTrigger className="h-9 bg-white text-sm">
                    <SelectValue placeholder="Select branch..." />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map((branch) => (
                      <SelectItem key={branch.id} value={branch.id}>
                        {branch.name} {(branch.is_main || branch.isMain) && '(Main)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Customer *</Label>
              <div className="flex gap-2">
                <Select value={selectedCustomerId} onValueChange={handleSelectCustomer}>
                  <SelectTrigger className="h-9 flex-1 bg-white text-sm">
                    <SelectValue placeholder="Choose customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customerOptions.map((option) => (
                      <SelectItem key={option.id} value={option.id}>
                        {option.name}{option.phone ? ` - ${option.phone}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button type="button" variant="outline" size="sm" className="h-9 px-3" onClick={() => setShowAddCustomerDialog(true)}>
                  Add
                </Button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Debtor Date</Label>
              <div className="[&_*]:text-sm [&_button]:h-9 [&_button]:bg-white">
                <DateTimePicker
                  value={layawayDate}
                  onChange={(date) => date && setLayawayDate(date)}
                  timezone={systemTimezone}
                  showTime={false}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Product Handling</Label>
              <Select value={productHandling} onValueChange={(value: 'hold' | 'handover') => setProductHandling(value)}>
                <SelectTrigger className="h-9 bg-white text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hold">Hold Product</SelectItem>
                  <SelectItem value="handover">Handover Product</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Schedule</Label>
              <Select value={paymentScheduleMonths.toString()} onValueChange={(v) => setPaymentScheduleMonths(parseInt(v))}>
                <SelectTrigger className="h-9 bg-white text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
                    <SelectItem key={m} value={m.toString()}>{m} month{m > 1 ? 's' : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Initial Payment</Label>
              <Input
                type="number"
                step="0.01"
                value={initialPayment === 0 ? '' : initialPayment}
                onChange={(e) => {
                  const value = e.target.value;
                  setInitialPayment(value === '' ? 0 : (parseFloat(value) || 0));
                }}
                placeholder="0.00"
                className="h-9 bg-white text-sm"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Payment Method</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger className="h-9 bg-white text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="transfer">Transfer</SelectItem>
                  <SelectItem value="mobile">Mobile Money</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-slate-950">
              <Package className="h-5 w-5 text-blue-600" />
              Invoice Items
            </h3>
            <div className="text-sm font-medium text-slate-500">
              {cart.length} item{cart.length === 1 ? '' : 's'} added
            </div>
          </div>

          <div className="p-4">
            <div className="rounded-lg border border-slate-200">
            <Table wrapperClassName="overflow-visible" className="min-w-[1260px]">
              <TableHeader>
                <TableRow className="bg-slate-100/80 hover:bg-slate-100/80">
                  <TableHead className="h-11 w-[480px] text-xs font-semibold uppercase tracking-wide text-slate-600">Item</TableHead>
                  <TableHead className="h-11 w-[160px] text-xs font-semibold uppercase tracking-wide text-slate-600">SKU</TableHead>
                  <TableHead className="h-11 w-[170px] text-xs font-semibold uppercase tracking-wide text-slate-600">Barcode</TableHead>
                  <TableHead className="h-11 w-[70px] text-center text-xs font-semibold uppercase tracking-wide text-slate-600">VAT</TableHead>
                  <TableHead className="h-11 w-[90px] text-center text-xs font-semibold uppercase tracking-wide text-slate-600">Qty</TableHead>
                  <TableHead className="h-11 w-[130px] text-right text-xs font-semibold uppercase tracking-wide text-slate-600">Avg Cost</TableHead>
                  <TableHead className="h-11 w-[130px] text-right text-xs font-semibold uppercase tracking-wide text-slate-600">Unit Price</TableHead>
                  <TableHead className="h-11 w-[130px] text-right text-xs font-semibold uppercase tracking-wide text-slate-600">Total</TableHead>
                  <TableHead className="h-11 w-[56px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow className="bg-blue-50/30 hover:bg-blue-50/40">
                  <TableCell className="relative p-3">
                    <Input
                      ref={itemNameInputRef}
                      value={itemSearchTerm}
                      onChange={(e) => {
                        setEntryItemId('');
                        setItemSearchTerm(e.target.value);
                        setEntryActiveField('name');
                      }}
                      onKeyDown={handleEntryKeyDown}
                      placeholder="Start typing item name"
                      autoComplete="off"
                      autoCorrect="off"
                      spellCheck={false}
                      autoCapitalize="none"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      data-form-type="other"
                      className="h-9 bg-white"
                    />
                    {itemSearchTerm && entrySuggestions.length > 0 && entryActiveField === 'name' ? (
                      <div className="absolute left-3 top-full z-50 mt-1 w-[720px] max-w-[90vw] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl">
                        <div ref={entrySuggestionsRef} className="max-h-[240px] overflow-y-auto">
                          {entrySuggestions.map((item, index) => (
                            <button
                              type="button"
                              key={item.id}
                              data-suggestion-index={index}
                              className={`flex w-full items-center justify-between px-3 py-2.5 text-left text-sm ${
                                index === entrySuggestionIndex ? 'bg-blue-50 text-blue-900' : 'hover:bg-slate-50'
                              }`}
                              onMouseDown={(event) => event.preventDefault()}
                              onClick={() => handleSelectEntryItem(item)}
                            >
                              <div>
                                <div className="font-medium">{item.name}</div>
                                <div className="text-xs text-slate-500">
                                  {item.sku}
                                  {item.barcode ? ` · ${item.barcode}` : ''}
                                  {item.availableQuantity !== undefined ? ` · Stock ${item.availableQuantity}` : ''}
                                </div>
                              </div>
                              <div className="text-xs text-slate-500">
                                {currencySymbol}{Number(item.selling_price || 0).toFixed(2)}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </TableCell>
                  <TableCell className="p-3">
                    <Input value={selectedEntrySku} readOnly className="h-9 bg-slate-50 text-slate-600" />
                  </TableCell>
                  <TableCell className="p-3">
                    <Input value={selectedEntryBarcode} readOnly className="h-9 bg-slate-50 text-slate-600" />
                  </TableCell>
                  <TableCell className="p-3 text-center">
                    <Checkbox
                      checked={entryVatEnabled}
                      onCheckedChange={(checked) => setEntryVatEnabled(checked === true)}
                    />
                  </TableCell>
                  <TableCell className="p-3">
                    <Input
                      type="number"
                      min="1"
                      className="mx-auto h-9 w-20 bg-white text-center"
                      value={entryQuantityText}
                      onChange={(e) => setEntryQuantityText(e.target.value)}
                      onKeyDown={handleEntryKeyDown}
                    />
                  </TableCell>
                  <TableCell className="p-3">
                    <Input
                      value={selectedEntryAverageCost ? selectedEntryAverageCost.toFixed(2) : ''}
                      readOnly
                      className="ml-auto h-9 w-24 bg-slate-50 text-right text-slate-600"
                    />
                  </TableCell>
                  <TableCell className="p-3">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      className="ml-auto h-9 w-24 bg-white text-right"
                      value={entryPriceText}
                      onChange={(e) => setEntryPriceText(e.target.value)}
                      onKeyDown={handleEntryKeyDown}
                    />
                  </TableCell>
                  <TableCell className="p-3 text-right font-semibold text-slate-900">{currencySymbol}{entryLineTotal.toFixed(2)}</TableCell>
                  <TableCell className="p-3">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={handleCommitEntryItem}
                      className="h-9 w-9 border-blue-100 bg-white text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                      disabled={
                        !selectedEntryItem ||
                        Number(selectedEntryItem.availableQuantity ?? 0) <= 0 ||
                        toQty(entryQuantityText) <= 0 ||
                        toPrice(entryPriceText) < 0
                      }
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>

                {cart.map((item) => (
                  <TableRow key={item.id} className="hover:bg-slate-50">
                    <TableCell className="p-3">
                      <div className="text-sm font-semibold text-slate-900">{item.name}</div>
                    </TableCell>
                    <TableCell className="p-3 text-sm text-slate-600">{item.sku}</TableCell>
                    <TableCell className="p-3 text-sm text-slate-600">{item.barcode || '-'}</TableCell>
                    <TableCell className="p-3 text-center">
                      <Checkbox
                        checked={Boolean(item.vat_enabled)}
                        onCheckedChange={(checked) => updateCartItemVat(item.id, checked === true)}
                      />
                    </TableCell>
                    <TableCell className="p-3">
                      <Input
                        type="number"
                        min="1"
                        className="mx-auto h-9 w-20 bg-white text-center"
                        value={item.quantity}
                        onChange={(e) =>
                          updateCartQuantity(item.id, parseInt(e.target.value) || 1)
                        }
                      />
                    </TableCell>
                    <TableCell className="p-3">
                      <div className="text-right text-sm font-medium text-slate-600">
                        {currencySymbol}{Number(item.buying_price || 0).toFixed(2)}
                      </div>
                    </TableCell>
                    <TableCell className="p-3">
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        className="ml-auto h-9 w-24 bg-white text-right"
                        value={item.price}
                        onChange={(e) =>
                          updateCartItemPrice(item.id, parseFloat(e.target.value) || 0)
                        }
                      />
                    </TableCell>
                    <TableCell className="p-3 text-right font-semibold text-slate-900">
                      {currencySymbol}
                      {lineTotal(parseFloat(item.price.toString()), item.quantity, item.vat_enabled).toFixed(2)}
                    </TableCell>
                    <TableCell className="p-3">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeFromCart(item.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          </div>

          <div className="border-t border-slate-200 bg-slate-50 px-5 py-4">
            <div className="ml-auto w-full max-w-md rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-slate-600">
                  <span>Subtotal</span>
                  <span className="font-medium text-slate-900">{currencySymbol}{cartSubtotal.toFixed(2)}</span>
                </div>
                {cartVAT > 0 && (
                  <div className="flex justify-between text-sm text-slate-600">
                    <span>VAT ({vatPercentage}%)</span>
                    <span className="font-medium text-slate-900">{currencySymbol}{cartVAT.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between border-t border-slate-200 pt-2 text-base font-bold text-slate-950">
                  <span>Total</span>
                  <span>{currencySymbol}{cartTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-slate-600">
                  <span>Initial Payment</span>
                  <span className="font-medium text-slate-900">{currencySymbol}{initialPayment.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-slate-600">
                  <span>Remaining</span>
                  <span className="font-semibold text-orange-700">{currencySymbol}{remainingBalance.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-slate-600">
                  <span>Monthly Payment</span>
                  <span className="font-medium text-slate-900">{currencySymbol}{monthlyPayment.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Notes</Label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional notes..."
            rows={2}
            className="min-h-[86px] bg-white"
          />
        </div>

        <div className="flex flex-wrap justify-end gap-2 border-t border-slate-200 pt-4">
          <Button
            onClick={createLayaway}
            disabled={cart.length === 0 || !customer.name.trim() || isCreating || initialPayment > cartTotal}
            className="h-10 min-w-48 bg-blue-600 font-semibold hover:bg-blue-700"
          >
            {isCreating ? 'Creating...' : 'Create Credit Sale'}
          </Button>
        </div>
      </form>

      <Dialog open={showAddCustomerDialog} onOpenChange={setShowAddCustomerDialog}>
        <DialogContent className="w-[95vw] max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Customer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={newCustomer.name}
                onChange={(e) => setNewCustomer((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Customer name"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={newCustomer.phone}
                onChange={(e) => setNewCustomer((prev) => ({ ...prev, phone: e.target.value }))}
                placeholder="Phone number"
              />
            </div>
            <div>
              <Label>Address</Label>
              <Input
                value={newCustomer.address}
                onChange={(e) => setNewCustomer((prev) => ({ ...prev, address: e.target.value }))}
                placeholder="Address"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowAddCustomerDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveCustomer} disabled={!newCustomer.name.trim()}>
                Save Customer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
